import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import NumberField from '../ui/NumberField';
import { useSettingsContext } from '../../hooks/useSettings';
import { Card, CardBody } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { SkeletonCard } from '../ui/Skeleton';
import { customerAPI, jobAPI, unwrapList } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import SelectMenu from '../ui/SelectMenu';

// PT-20 (2026-07-24) 20.1: local ITEM_CATEGORIES hardcode DELETED. Category options
// now source from Rule-10 (Seed/Settings first) via `getOptions('categories')` —
// backed by useSettings.js LOCAL_DEFAULTS.categories (insulation/drywall/fireproofing/
// acoustical/spray_foam/custom) with tenant Settings overrides when present.

// IDI-578 correction: lines embedded by migration carry this source.
const MIGRATED_SOURCE = 'idi20_embed_from_line_item';
function isMigratedLine(l) {
  const src = l?.source;
  return src === MIGRATED_SOURCE || src?.S === MIGRATED_SOURCE;
}

export default function JobForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { getOptions } = useSettingsContext();
  const isEdit = Boolean(id);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // IDI-565 (D1, D4): a signed job's totals and lines are the server's.
  const [isSigned, setIsSigned] = useState(false);
  // IDI-578 correction: a migrated line's lineTotal is its cost, not its price
  // (embed-job-lineitems.py). IDI-592 (D1): migrated = job legacyId, or any
  // migrated line. Its total is never Edit job's (D2).
  const [isMigrated, setIsMigrated] = useState(false);

  const [formData, setFormData] = useState({
    // PT-20 CLEANUP-8 (2026-07-24): `jobType` added so the form controls the value
    // that Job Detail + Jobs List already render via getEnumLabel('job_types', ...).
    // Rule-10 (Seed/Settings first) via useSettingsContext().getOptions('job_types').
    title: '', customerId: '', customerName: '', description: '',
    jobType: '',
    lineItems: [{ id: `li-${Date.now()}`, category: 'spray_foam', item_name: '', quantity: 1, unit_rate: 0, line_total: 0 }],
  });

  useEffect(() => {
    // Redirect non-admin users trying to create a new job
    if (!isEdit && user && user.role !== 'admin') {
      navigate('/quotes', { state: { message: 'Jobs are created from Won quotes — start a quote here' } });
      return;
    }
    loadCustomers();
    if (isEdit) loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async () => {
    try {
      const r = await customerAPI.getCustomers();
      // IDI-100: use the shared unwrapper. `r?.data || []` never checked the
      // enveloped shape's `customers` key — harmless here only because this
      // call passes no `limit` and gets a raw array back today.
      setCustomers(unwrapList(r, 'customers', 'JobForm.loadCustomers').list);
    } catch (err) {
      // PT-33.6 (2026-07-27): surface the failure via the page-level Alert
      // rather than the previous silent catch. Without customers loaded the
      // required Customer field cannot be selected — the operator must know.
      setError('Failed to load customers. Please refresh the page.');
    }
  };

  const loadJob = async () => {
    try {
      setLoading(true);
      const job = await api.getJob(id);
      setFormData({
        title: job.title || '', customerId: job.customer_id || job.customerId || '',
        customerName: job.customer_name || job.customerName || '', description: job.description || '',
        jobType: job.job_type || job.jobType || '',
        lineItems: job.line_items || job.lineItems || [],
      });
      setIsSigned((job.lockedTotalCents ?? job.locked_total_cents) != null);
      const lines = job.line_items || job.lineItems || [];
      setIsMigrated(Boolean(job.legacyId ?? job.legacy_id)
        || (Array.isArray(lines) && lines.some(isMigratedLine)));
    } catch { setError('Failed to load job.'); }
    finally { setLoading(false); }
  };

  const handleLineItemChange = (idx, field, value) => {
    setFormData(prev => {
      const items = [...prev.lineItems];
      const before = items[idx];
      let seeded = {};
      // IDI-578 (D2): a migrated line has lineTotal but no unit_rate. Before a
      // qty/rate edit, derive its rate as lineTotal ÷ quantity (IDI-257 backfill)
      // so the recompute below does not zero the line.
      if ((field === 'unit_rate' || field === 'quantity') && typeof before.unit_rate !== 'number') {
        const base = before.line_total ?? before.lineTotal;
        const prevQty = parseFloat(before.quantity) || 0;
        if (typeof base === 'number' && prevQty > 0) seeded = { unit_rate: Math.round(base / prevQty) };
      }
      items[idx] = { ...before, ...seeded, [field]: value };
      if (field === 'unit_rate' || field === 'quantity') {
        // IT-R4-011b: Rate is stored in cents, input is in dollars
        const rate = parseFloat(items[idx].unit_rate) || 0;
        const qty = parseFloat(items[idx].quantity) || 0;
        items[idx].line_total = Math.round(rate * qty);
      }
      return { ...prev, lineItems: items };
    });
  };

  const addLineItem = () => setFormData(prev => ({ ...prev, lineItems: [...prev.lineItems, { id: `li-${Date.now()}`, category: 'spray_foam', item_name: '', quantity: 1, unit_rate: 0, line_total: 0 }] }));
  const removeLineItem = (idx) => setFormData(prev => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId) { setError('Customer is required.'); return; }
    if (!formData.title) { setError('Title is required.'); return; }

    setSaving(true);
    const total = formData.lineItems.reduce((s, i) => s + (i.line_total ?? i.lineTotal ?? 0), 0);
    // IDI-578 (D1): line_total, else lineTotal — as the server's calculateJobTotal.
    // PT-20 CLEANUP-8 (2026-07-24): include job_type in the payload so Jobs List's
    // JOB TYPE column persists across reload. Sends both snake_case + camelCase for
    // defense-in-depth (matches customer_id/customerId pattern elsewhere in the form).
    // IDI-565 (D1, D4): editing a signed job never sends a total or lines.
    // IDI-592 (D2): a migrated job (mixed included) sends its lines but never a
    // total — a migrated lineTotal is cost; the server keeps its stored total.
    const rest = { ...formData };
    delete rest.lineItems;
    let payload;
    if (isEdit && isSigned) payload = { ...rest, job_type: formData.jobType };
    else if (isEdit && isMigrated) payload = { ...formData, job_type: formData.jobType };
    else payload = {
        ...formData,
        totalAmount: total,
        total_amount: total,
        job_type: formData.jobType,
    };

    try {
      if (isEdit) { await jobAPI.updateJob(id, payload); }
      else { await jobAPI.createJob(payload); }
      navigate('/jobs');
    } catch { setError('Failed to save job.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-4 p-6"><SkeletonCard /><SkeletonCard /></div>;

  return (
    <div className="max-w-4xl mx-auto">
      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input label="Title *" value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} required />
              <div>
                <label className="label">Customer *</label>
                <SelectMenu
                  value={formData.customerId}
                  onChange={(val) => { const c = customers.find(x => x.id === val); setFormData(p => ({ ...p, customerId: val, customerName: c?.name || '' })); }}
                  placeholder="Select customer..."
                  options={customers.map(c => ({ value: c.id, label: c.name }))}
                />
              </div>
              <div>
                {/* PT-20 CLEANUP-8 (2026-07-24): Job Type field per Rule-10 (Seed/Settings first).
                    Options source: getOptions('job_types') — backed by `job_type.v1` seed in
                    vocabSeeds.js. What Jobs List + Job Detail already RENDER via
                    getEnumLabel('job_types', …), JobForm now CONTROLS. */}
                <label className="label">Job Type</label>
                <SelectMenu
                  value={formData.jobType}
                  onChange={(val) => setFormData(p => ({ ...p, jobType: val }))}
                  placeholder="Select type..."
                  options={getOptions('job_types')}
                />
              </div>
              {/* IDI-246: no Status here. A job's status reads from its work
                  orders, and Completed is the Job page's Complete Job. */}
            </div>
            <div className="mt-3">
              <label className="label">Description</label>
              <Textarea
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              />
            </div>
          </CardBody>
        </Card>

        {/* Line Items */}
        <Card className="mb-4">
          <CardBody>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Line Items</h3>
              {!(isEdit && isSigned) && (
                <Button type="button" variant="secondary" size="sm" onClick={addLineItem}><PlusIcon className="h-4 w-4" />Add</Button>
              )}
            </div>
            {isEdit && isSigned ? (
              <div data-testid="signed-job-lines">
                <p className="text-xs text-gray-500 mb-2">Signed job — change lines with a change order.</p>
                <div className="space-y-1">
                  {formData.lineItems.map((item, idx) => (
                    <div key={item.id || idx} className="grid grid-cols-5 gap-2 border border-gray-100 rounded p-2 text-sm">
                      <span className="text-gray-500">{item.category || ''}</span>
                      <span className="col-span-2">{item.item_name || item.name || item.description || ''}</span>
                      <span>{item.quantity ?? ''}</span>
                      <span className="font-semibold text-right">${((item.line_total ?? item.lineTotal ?? 0) / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <div className="space-y-2" data-testid={isEdit && isMigrated ? 'migrated-job-lines' : undefined}>
              {isEdit && isMigrated && (
                <p className="text-xs text-gray-500 mb-2">Migrated lines are read-only — the job&apos;s price is on its quote.</p>
              )}
              {formData.lineItems.map((item, idx) => isMigratedLine(item) ? (
                // IDI-592 (D3): a migrated line is read-only; its lineTotal is cost.
                <div key={item.id || idx} data-testid="migrated-line" className="grid grid-cols-5 gap-2 border border-gray-100 rounded p-2 text-sm">
                  <span className="text-gray-500">{item.category || ''}</span>
                  <span className="col-span-2">{item.item_name || item.name || item.description || ''}</span>
                  <span>{item.quantity ?? ''}</span>
                  <span className="text-right text-gray-400">—</span>
                </div>
              ) : (
                <div key={item.id || idx} className="grid grid-cols-5 gap-2 items-end border border-gray-100 rounded p-2">
                  <div>
                    <label className="text-xs text-gray-500">Category</label>
                    <SelectMenu
                      value={item.category}
                      onChange={(val) => handleLineItemChange(idx, 'category', val)}
                      size="sm"
                      options={getOptions('categories')}
                      // PT-20 20.1: Rule-10 (Seed/Settings first) — was ITEM_CATEGORIES.map(...)
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Name</label>
                    <Input
                      value={item.item_name || ''}
                      onChange={(e) => handleLineItemChange(idx, 'item_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Qty</label>
                    <NumberField
                      kind="integer"
                      min={0}
                      value={item.quantity ?? null}
                      onChange={(n) => handleLineItemChange(idx, 'quantity', n ?? '')}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Rate ($)</label>
                    {/* PT-20 20.3: NumberField emits dollar-float; keep the cents
                        conversion at the caller (cents↔dollars) per the atom's docstring. */}
                    <NumberField
                      kind="currency"
                      min={0}
                      value={typeof item.unit_rate === 'number' ? item.unit_rate / 100 : null}
                      onChange={(n) => handleLineItemChange(
                        idx,
                        'unit_rate',
                        typeof n === 'number' ? Math.round(n * 100) : 0
                      )}
                    />
                  </div>
                  <div className="flex items-end gap-1">
                    <span className="text-xs font-semibold flex-1">${((item.line_total ?? 0) / 100).toFixed(2)}</span>
                    <button type="button" onClick={() => removeLineItem(idx)} className="p-1 text-red-400 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardBody>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate('/jobs')}>Cancel</Button>
          <Button type="submit" loading={saving}>{saving ? 'Saving...' : isEdit ? 'Update Job' : 'Create Job'}</Button>
        </div>
      </form>
    </div>
  );
}
